<template>
  <div>
    <sdPageHeader title="Monitoring Laporan Kinerja" />
    <Main>
      <!-- Filter Section -->
      <a-row :gutter="25" class="mb-25">
        <a-col :span="24">
          <sdCards headless>
            <a-row :gutter="16">
              <a-col span="24" style="text-align: right;">
                <a-button type="primary" @click="refreshData">
                  <template #icon><ReloadOutlined /></template>
                  Refresh Data
                </a-button>
              </a-col>
            </a-row>
          </sdCards>
        </a-col>
      </a-row>
      <!-- Monitoring Table Section -->
      <a-row :gutter="25">
        <a-col :span="24">
          <sdCards title="Status Pelaporan" bodypadding="0px">
            <a-table
              :columns="monitoringColumns"
              :data-source="dataMonitoring"
              :pagination="false"
              :scroll="{ x: 1000 }"
              :loading="isLoading"
              bordered
            />
          </sdCards>
        </a-col>
      </a-row>
      <a-row :gutter="25">
        <sdCards title="Grafik Ketepatan Waktu" bodypadding="0px">
          <div v-if="isLoading" class="spin">
            <a-spin />
          </div>
          <div v-else>
            <a-row :gutter="24">
              <template v-for="(item, index) in monitoringData" :key="index">
                <a-col :span="4" style="margin-bottom: 20px">
                  <div style="height: 200px;margin-bottom: 20px">
                    <Doughnut :data="pieChartData(item)" :options="pieChartOptions" />
                    <div class="chart-title">{{ item.entitas ? item.entitas.nama : 'Entitas ' + (index + 1) }}</div>
                  </div>
                </a-col>
              </template>
            </a-row>
            <div style="padding: 20px;"></div>
            <a-row :gutter="24">
              <a-col :span="12">
                <a-table :pagination="false" :columns="ketepatanColumn" :data-source="ketepatanData" bordered />
              </a-col>
            </a-row>
          </div>
        </sdCards>
      </a-row>
    </Main>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { Main } from '@/view/styled';
import { ReloadOutlined } from '@ant-design/icons-vue';
import { renderDownloadButton } from '@/utility/renderDownloadButton';
import { renderStatusTag } from '@/utility/renderStatusTag';
import { getItem, setItem } from '@/utility/localStorageControl';
import axiosApi from '@/api';
import { notification } from 'ant-design-vue';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { MONTH_OPTIONS } from '@/constants/monthOptions';
import ChartDataLabels from 'chartjs-plugin-datalabels';
ChartJS.register(Title, Tooltip, Legend, ArcElement, ChartDataLabels);

export default {
  name: 'NkoMonitoring',
  components: {
    Main,
    ReloadOutlined,
    Doughnut,
  },
  setup() {
    const isLoading = ref(false);
    const bulanOptions = MONTH_OPTIONS;
    const { filterMonths } = require('@/utility/monthsData');
    const currentMonth = new Date().getMonth() + 1; // JavaScript months are 0-indexed
    const monthsFilter = filterMonths(currentMonth);
    const currentYear = new Date().getFullYear();
    const tahunOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const filters = reactive({
      bulan: new Date().getMonth() + 1,
      tahun: currentYear,
    });

    // Table columns for monitoring
    const monitoringColumns = [
      {
        title: 'No',
        dataIndex: 'no',
        key: 'no',
        width: 60,
      },
      {
        title: 'Nama Entitas',
        dataIndex: 'nama_entitas',
        key: 'nama_entitas',
        width: 250,
      },
      {
        title: 'Bulan',
        children: monthsFilter
          .map(month => [
            {
              title: month.full,
              dataIndex: `status_${month.index}`,
              key: `status_${month.index}`,
              align: 'center',
              colSpan: 3,
              width: 150,
            },
            {
              title: '',
              dataIndex: `tanggal_${month.index}`,
              key: `tanggal_${month.index}`,
              align: 'center',
              colSpan: 0,
              width: 150,
            },
            {
              title: '',
              dataIndex: `file_${month.index}`,
              key: `file_${month.index}`,
              align: 'center',
              colSpan: 0,
              width: 150,
            },
          ])
          .flat(),
      },
    ];

    const ketepatanColumn = [
      {
        title: 'No',
        dataIndex: 'no',
        key: 'no',
        width: 60,
      },
      {
        title: 'Anak Perusahaan',
        dataIndex: 'nama_entitas',
        key: 'nama_entitas',
        width: 250,
      },
      {
        title: 'Tepat',
        dataIndex: 'tepat',
        key: 'tepat',
        align: 'center',
      },
      {
        title: 'Terlambat',
        dataIndex: 'terlambat',
        key: 'terlambat',
        align: 'center',
      },
      {
        title: 'Total Bulan',
        dataIndex: 'total_bulan',
        key: 'total_bulan',
        align: 'center',
      },
    ];

    // Sample data for monitoring table
    const monitoringData = ref([]);

    const ketepatanData = computed(() => {
      return !monitoringData.value
        ? null
        : monitoringData.value.map((item, index) => {
            return {
              no: index + 1,
              nama_entitas: item.entitas.nama,
              tepat: item.chartData[0].jtepat,
              terlambat: item.chartData[0].jterlambat,
              total_bulan: item.chartData[0].total_bulan,
            };
          });
    });

    // Use the extracted utility function for download buttons

    const dataMonitoring = computed(() => {
      if (!monitoringData.value) return null;

      return monitoringData.value.map((item, index) => {
        // Use the extracted utility function for status tags

        return {
          ...(item.data
            ? item.data.reduce((acc, target) => {
                acc[`status_${target.bulan}`] = renderStatusTag(target.status_laporan);
                acc[`tanggal_${target.bulan}`] = target.tanggal_laporan;
                acc[`file_${target.bulan}`] = renderDownloadButton(target.file, downloadFile);
                return acc;
              }, {})
            : {}),
          no: index + 1,
          nama_entitas: item.entitas.nama,
        };
      });
    });

    // Evidence Modal
    const evidenceModal = reactive({
      visible: false,
      entityName: '',
      entityId: null,
      files: [],
    });

    const viewEvidence = record => {
      evidenceModal.visible = true;
      evidenceModal.entityName = record.namaEntitas;
      evidenceModal.entityId = record.key;

      evidenceModal.files = [
        { id: 1, name: 'Laporan_Kinerja_' + record.namaEntitas.replace(/\s+/g, '_') + '.pdf', url: '#' },
        { id: 2, name: 'Evidens_' + record.namaEntitas.replace(/\s+/g, '_') + '.xlsx', url: '#' },
      ];
    };

    const downloadFile = async file => {
      try {
        const url = process.env.VUE_APP_API_BASE_URL + '/api/generate-download-url';
        const response = await axiosApi.post(url, {
          path: file,
        });
        const downloadUrl = response.data;
        window.open(downloadUrl, '_blank');
      } catch (error) {
        console.error('Error generating download URL:', error);
        notification.error({
          message: 'Error',
          description: 'Gagal mendapatkan URL untuk download file.',
        });
      }
    };

    const handleEvidenceOk = () => {
      evidenceModal.visible = false;
    };

    const handleEvidenceCancel = () => {
      evidenceModal.visible = false;
    };

    // Function to get status class
    const getStatusClass = status => {
      if (!status) return '';
      if (status.toLowerCase().includes('tepat')) {
        return 'status-tepat';
      } else if (status.toLowerCase().includes('terlambat')) {
        return 'status-terlambat';
      }
      return '';
    };

    const refreshData = () => {
      setItem('bulan', filters.bulan);
      setItem('tahun', filters.tahun);
      setTimeout(() => {
        getData(filters.bulan, filters.tahun);
      }, 300);
    };

    onMounted(() => {
      filters.bulan = getItem('bulan');
      filters.tahun = getItem('tahun');
      refreshData();
    });

    // Function to generate chart data for each entity
    const getChartDataForEntity = entity => {
      let tepatCount = 0;
      let terlambatCount = 0;
      let belumCount = 0;

      tepatCount = Math.round((entity.chartData[0].jtepat / entity.chartData[0].total_bulan) * 100);
      terlambatCount = Math.round((entity.chartData[0].jterlambat / entity.chartData[0].total_bulan) * 100);
      belumCount = Math.round((entity.chartData[0].jbelum / entity.chartData[0].total_bulan) * 100);

      return {
        labels: ['Tepat Waktu', 'Terlambat', 'Belum Ada Laporan'],
        datasets: [
          {
            data: [tepatCount, terlambatCount, belumCount],
            backgroundColor: ['#155d82', '#e5773c', '#3493b5'],
            hoverBackgroundColor: ['#155d82', '#e5773c', '#3493b5'],
          },
        ],
      };
    };

    // Pie chart data function
    const pieChartData = entity => {
      return getChartDataForEntity(entity);
    };

    // Function to get chart options for each chart
    const getChartOptions = () => {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            color: '#161617',
            formatter: (value, context) => {
              const label = context.chart.data.labels[context.dataIndex];
              if (value == 0) {
                return '';
              }
              return `${label}\n${value}%`;
            },
            font: {
              weight: 'bold',
              size: 12,
            },
          },
        },
      };
    };

    // Create options for each chart
    const pieChartOptions = getChartOptions();

    const getData = (bulan, tahun) => {
      isLoading.value = true;
      const accessToken = getItem('access_token');
      const entitas = getItem('entitas');

      axiosApi.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      axiosApi
        .get('monitoring?month=' + bulan + '&year=' + tahun + '&entitas_id=' + entitas)
        .then(response => {
          monitoringData.value = response.data;
          isLoading.value = false;
        })
        .catch(error => {
          console.error('Error fetching data:', error);
          notification.error({
            message: 'Gagal mengambil data',
            description: error.message || 'Terjadi kesalahan saat mengambil data.',
          });
          isLoading.value = false;
        });
    };
    return {
      filters,
      bulanOptions,
      tahunOptions,
      monitoringColumns,
      monitoringData,
      evidenceModal,
      viewEvidence,
      downloadFile,
      handleEvidenceOk,
      handleEvidenceCancel,
      getStatusClass,
      refreshData,
      dataMonitoring,
      isLoading,
      pieChartData,
      pieChartOptions,
      ketepatanColumn,
      ketepatanData,
    };
  },
};
</script>

<style scoped>
.mb-25 {
  margin-bottom: 25px;
}

.mt-25 {
  margin-top: 25px;
}

/* Status colors */
.status-tepat {
  color: #008000;
  font-weight: bold;
}

.status-terlambat {
  color: #ff0000;
  font-weight: bold;
}

.status-tepat-bg {
  background-color: #008000;
}

.status-terlambat-bg {
  background-color: #ff0000;
}

/* Chart legend styling */
.chart-legend {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 30px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.legend-color {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: 8px;
}

/* Chart title styling */
.chart-title {
  text-align: center;
  font-weight: bold;
  margin-top: 10px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Evidence modal styling */
.evidence-file {
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.file-name {
  margin-left: 10px;
  flex-grow: 1;
}

.no-evidence {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  color: #999;
}

::v-deep(.table-row-minor) {
  background-color: #fada7a;
  font-weight: bold;
  font-size: 14px;
}

::v-deep .ant-table-thead > tr > th {
  background-color: #578fca; /* navy */
  color: white;
}
</style>
